// src/Payroll_Management/PayRoll_Report/BataAbstractReportPrint.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, Printer, RefreshCcw } from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}
function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}
function fmt2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return s(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// accepts yyyy-mm-dd OR dd/MM/yyyy and returns dd/MM/yyyy for display
function toDDMMYYYY(input) {
  const v = s(input);
  if (!v) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return v;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [yy, mm, dd] = v.split("-");
    return `${dd}/${mm}/${yy}`;
  }
  // last fallback: try Date parse
  const dt = new Date(v);
  if (!Number.isNaN(dt.getTime())) {
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    return `${dd}/${mm}/${yy}`;
  }
  return v;
}

const BataAbstractReportPrint = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const navigate = useNavigate();
  const location = useLocation();

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // read filters from querystring
  const q = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const finYearId = s(q.get("finYearId")) || localStorage.getItem("finYearId") || "";
  const loginBranchId =
    s(q.get("loginBranchId")) ||
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    s(q.get("branchType")) ||
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";

  const canViewAll = s(branchType).toUpperCase() === "HO";

  const branchIdParam = s(q.get("branchId")) || "Select";
  const depNameParam = s(q.get("depName")); // optional
  const depCodeParam = s(q.get("depCode")); // optional
  const fromDateParam = s(q.get("fromDate"));
  const toDateParam = s(q.get("toDate"));

  const effectiveBranchId = canViewAll ? branchIdParam : loginBranchId;

  // meta + print rows
  const [metaLoading, setMetaLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [companyName, setCompanyName] = useState("Nissi Infotech");
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-32 (PRINT)", []);

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const loadMeta = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing login branch info. Please re-login.");
      return;
    }

    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/bata-abstract-report/meta`, {
        params: {
          finYearId: toInt(finYearId),
          branchId: toInt(loginBranchId),
          branchType: s(branchType),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setBranches(res.data.branches || []);

      // If you already expose companyName in meta, set it here.
      // Otherwise we keep fallback "Nissi Infotech".
      if (res.data.companyName) setCompanyName(res.data.companyName);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Meta error");
    } finally {
      setMetaLoading(false);
    }
  };

  const loadPrintData = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(loginBranchId) || !s(branchType)) {
      toast.warn("Missing login branch info. Please re-login.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bata-abstract-report/print-data`, {
        params: {
          finYearId: toInt(finYearId),
          loginBranchId: s(loginBranchId),
          branchType: s(branchType),
          branchId: s(effectiveBranchId),

          // same filters as main list
          depName: s(depNameParam),
          depCode: s(depCodeParam),

          fromDate: s(fromDateParam),
          toDate: s(toDateParam),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load print data");
        return;
      }

      setRows(res.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Print data error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branchLabel = useMemo(() => {
    const b =
      branches.find((x) => String(x.fld_branchid) === String(effectiveBranchId)) ||
      branches.find((x) => String(x.fld_branchid) === String(loginBranchId));
    return s(b?.fld_branchname) || (canViewAll ? "Select" : "Login Branch");
  }, [branches, effectiveBranchId, loginBranchId, canViewAll]);

  const totalAmount = useMemo(() => {
    return (rows || []).reduce((sum, r) => {
      const v = Number(r?.fld_totalamount);
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);
  }, [rows]);

  const onPrint = () => window.print();
  const onBack = () => navigate(-1);
  const onReload = async () => {
    await loadMeta();
    await loadPrintData();
  };

  const fromDisp = toDDMMYYYY(fromDateParam);
  const toDisp = toDDMMYYYY(toDateParam);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        {/* Top header (NOT printed) */}
        <div className="print:hidden">
          <HeaderBar
            title={<span className="font-dmserif text-3xl">BATA ABSTRACT REPORT - PRINT</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />

          <div
            className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : `${currentSkin.glass} bg-white text-black border-gray-200`
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="text-sm font-bold">
                <span
                  className={`px-4 py-2 rounded-2xl ${
                    isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {screenBadge}
                </span>
              </div>

              <div className="flex flex-wrap gap-3 justify-end">
                <button onClick={onBack} className={ghostBtn}>
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <button onClick={onReload} className={ghostBtn} disabled={loading || metaLoading}>
                  <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Reload
                </button>

                <button onClick={onPrint} className={primaryBtn} disabled={loading || metaLoading}>
                  <Printer className="w-4 h-4" />
                  Print
                </button>
              </div>
            </div>

            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Click <b>Print</b> to open the browser print dialog (same tab).
            </div>
          </div>
        </div>

        {/* Printable Area */}
        <div className="mx-auto max-w-5xl mt-6">
          <div className="print-area border border-black bg-white text-black">
            {/* Header box */}
            <div className="p-4 border-b border-black">
              <div className="flex gap-4 items-start">
                {/* Logo */}
                <div className="w-28 h-20 border border-black flex items-center justify-center text-[10px]">
                  <img
                    src="/assets/company-logo.png"
                    alt="Company Logo"
                    className="max-w-full max-h-full"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <span className="logo-fallback">LOGO</span>
                </div>

                <div className="flex-1">
                  <div className="text-center leading-tight">
                    <div className="text-xl font-extrabold">{companyName || "Company Name"}</div>
                    <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>

                    {/* Keep placeholders unless you add address/phone into backend meta */}
                    <div className="text-xs mt-1">
                      <div>Address Line 1, Address Line 2</div>
                      <div>
                        Phone No : xxxxxxxxxx &nbsp;&nbsp; Email : xxxxx@xxxxx.com
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 border-t border-black pt-2">
                <div className="text-center font-extrabold text-[13px] uppercase">
                  BATA ABSTRACT REPORT AS ON {fromDisp || "____"} to {toDisp || "____"}
                </div>
              </div>
            </div>

            {/* Branch line */}
            <div className="px-4 py-2 border-b border-black text-sm font-bold">
              Branch : {s(branchLabel).toUpperCase()}
            </div>

            {/* Table */}
            <div className="p-4">
              <table className="w-full border-collapse" style={{ border: "1px solid #000" }}>
                <thead>
                  <tr>
                    <th className="th">S.No</th>
                    <th className="th">Department Code</th>
                    <th className="th">Department Name</th>
                    <th className="th">Total Amount</th>
                    <th className="th">Remarks</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td className="td text-center" colSpan={5}>
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td className="td text-center" colSpan={5}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    <>
                      {rows.map((r, idx) => (
                        <tr key={`${r.fld_deptid}-${idx}`}>
                          <td className="td text-center">{idx + 1}</td>
                          <td className="td text-center">{r.fld_deptcode}</td>
                          <td className="td">{r.fld_departmentname}</td>
                          <td className="td text-right">{fmt2(r.fld_totalamount)}</td>
                          <td className="td" />
                        </tr>
                      ))}

                      {/* Total row like screenshot */}
                      <tr>
                        <td className="td" colSpan={2} />
                        <td className="td font-bold text-center">Total</td>
                        <td className="td font-bold text-right">{fmt2(totalAmount)}</td>
                        <td className="td" />
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer signatures */}
            <div className="p-4 pt-10">
              <div className="flex justify-between text-sm font-bold">
                <div>Prepared By</div>
                <div>Checked By</div>
                <div>Authorized Signature</div>
              </div>
            </div>
          </div>
        </div>

        {/* Print styling */}
        <style>{`
          .print-area { box-shadow: none; }
          .logo-fallback { display:none; }

          .th {
            border: 1px solid #000;
            padding: 8px 6px;
            font-size: 12px;
            font-weight: 700;
            text-align: center;
            background: #fff;
          }
          .td {
            border: 1px solid #000;
            padding: 8px 6px;
            font-size: 12px;
          }

          @media print {
            .print\\:hidden { display: none !important; }
            body { background: #fff !important; }
            .min-h-screen { padding: 0 !important; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
          }
        `}</style>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default BataAbstractReportPrint;
